import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { cleanParams } from '@/utils/cleanParams';

import { type InvestmentOwner, OwnerTypes, type TransactionType } from '../fund/types';
import { apiBffFetch } from '../index';
import type { SortDirectionType } from '../types';

export const TRANSACTION_VIEW_QUERY_KEYS = {
  transactions: 'transactions',
  transaction: 'transaction',
} as const;

export enum TransactionViewDirection {
  Incoming = 'Incoming',
  Outgoing = 'Outgoing',
  TwoWay = 'TwoWay',
}

export enum TransactionViewCounterpartyType {
  Fund = 'Fund',
  Wallet = 'Wallet',
}

export enum TransactionFeePayer {
  Wallet = 'wallet',
  Sender = 'sender',
  Sponsor = 'sponsor',
}

export enum TransactionOnchainStatus {
  Success = 'success',
  Failure = 'failure',
}

export interface TransactionViewCounterparty {
  type: TransactionViewCounterpartyType;
  id: string;
  name: string | null;
}

export interface TransactionViewLine {
  amount: string | null;
  amountInUsd: string | null;
  denomination: string | null;
  iconUrl: string | null;
  counterparty: TransactionViewCounterparty | null;
}

export interface TransactionViewFee {
  amount: string | null;
  amountInUsd: string | null;
  denomination: string | null;
  iconUrl: string | null;
  payer: TransactionFeePayer;
}

export interface TransactionViewOnchain {
  hash: string;
  status: TransactionOnchainStatus | null;
}

export interface TransactionApiItem {
  id: string;
  date: string;
  direction: TransactionViewDirection | null;
  type: TransactionType;
  notes: string | null;
  lines: TransactionViewLine[];
  fee: TransactionViewFee | null;
  onchain: TransactionViewOnchain | null;
}

export type TransactionViewOrderBy = 'counterparty' | 'date' | 'direction' | 'amount' | 'type';

export type TransactionViewsParams = {
  search?: string;
  dateFrom?: string;
  dateTo?: string;
  direction?: TransactionViewDirection;
  denomination?: string;
  type?: TransactionType;
  page?: number;
  limit?: number;
  orderBy?: TransactionViewOrderBy;
  orderDirection?: SortDirectionType;
} & InvestmentOwner;

export type TransactionsApiResponse = {
  transactions: TransactionApiItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNext: boolean;
  };
};

export interface TransactionFilterOptionsApiResponse {
  directions: TransactionViewDirection[];
  denominations: string[];
  types: TransactionType[];
}

export const getTransactionHashLookupOptions = (hash: string) => {
  const params = {
    ownerType: OwnerTypes.USER,
    search: hash,
    page: 1,
    limit: 1,
  } satisfies TransactionViewsParams;

  return queryOptions({
    queryKey: [TRANSACTION_VIEW_QUERY_KEYS.transactions, 'submitted-hash', hash],
    queryFn: () =>
      apiBffFetch<TransactionsApiResponse>('/transaction-views', {
        params,
        onResponseError: () => undefined,
      }),
    retry: false,
  });
};

export const getTransactionsInfiniteOptions = (
  params?: Omit<TransactionViewsParams, 'page' | 'limit'>,
) => {
  const safeParams = cleanParams(params) ?? {};

  return infiniteQueryOptions({
    queryKey: [TRANSACTION_VIEW_QUERY_KEYS.transactions, 'infinite', safeParams],
    queryFn: ({ pageParam = 1 }) => {
      const pageParams = cleanParams({ ...safeParams, page: pageParam, limit: 10 });
      return apiBffFetch<TransactionsApiResponse>('/transaction-views', { params: pageParams });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => (lastPage.meta.hasNext ? lastPage.meta.page + 1 : undefined),
    refetchOnMount: true,
  });
};

export const getTransactionFilterOptionsOptions = (params?: InvestmentOwner) => {
  const safeParams = cleanParams(params) ?? {};

  return queryOptions({
    queryKey: [TRANSACTION_VIEW_QUERY_KEYS.transactions, 'filter-options', safeParams],
    queryFn: () =>
      apiBffFetch<TransactionFilterOptionsApiResponse>('/transaction-views/filter-options', {
        params: safeParams,
      }),
  });
};

export const getTransactionDetailOptions = (id: string) =>
  queryOptions({
    queryKey: [TRANSACTION_VIEW_QUERY_KEYS.transaction, id],
    queryFn: () => apiBffFetch<TransactionApiItem>(`/transaction-views/${id}`),
    enabled: Boolean(id),
  });
